import { FIXED_TIMESTEP, MAX_FRAME_DELTA, MAX_SUBSTEPS } from '../game/constants';

/**
 * Fixed-timestep game loop.
 *
 * Physics must not be driven by the raw frame delta: a 120 Hz display would
 * integrate gravity in half the steps a 60 Hz display does, and the ball would
 * behave differently on different hardware. Instead, rendered frames accumulate
 * elapsed time and the simulation is stepped in whole 1/60 s units, which is
 * also what Rapier's kinematic-velocity derivation expects.
 *
 * `MAX_SUBSTEPS` caps the catch-up work after a stall (a background tab, a GC
 * pause). Any backlog beyond that is dropped rather than simulated, which keeps
 * a slow frame from snowballing into a death spiral.
 */
export class GameLoop {
  private rafId = 0;
  private lastMs = 0;
  private accumulator = 0;
  private running = false;

  constructor(
    private readonly onFixedStep: (dt: number) => void,
    private readonly onRender: () => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastMs = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    if (this.rafId !== 0) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  get isRunning(): boolean {
    return this.running;
  }

  private readonly tick = (nowMs: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.tick);

    let delta = (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    if (!Number.isFinite(delta) || delta < 0) delta = 0;
    delta = Math.min(delta, MAX_FRAME_DELTA);

    this.accumulator += delta;

    let steps = 0;
    while (this.accumulator >= FIXED_TIMESTEP && steps < MAX_SUBSTEPS) {
      this.onFixedStep(FIXED_TIMESTEP);
      this.accumulator -= FIXED_TIMESTEP;
      steps += 1;
    }

    if (steps >= MAX_SUBSTEPS) {
      // Give up on the backlog instead of trying to catch up forever.
      this.accumulator = 0;
    }

    this.onRender();
  };
}
